import { isNativeApp } from '@/lib/capacitor';

/**
 * Garde-fou « clavier intempestif » pour la WebView Sunmi.
 *
 * Sur device, tout focus PROGRAMMATIQUE d'un champ éditable (autoFocus,
 * FocusScope Radix à l'ouverture d'un Dialog, `.focus()` au montage) fait
 * surgir le clavier Android en plein écran, souvent par-dessus la liste ou
 * le bouton que l'utilisateur cherchait (retour terrain WAVE 2026-09-10,
 * dialogue « Récupérer une table »).
 *
 * Règle : sur natif, un champ ne prend le focus que si l'utilisateur vient
 * d'interagir (tap, clic, touche). Un focus qui arrive sans interaction
 * récente est annulé (blur immédiat). Opt-out explicite pour les rares
 * champs qui DOIVENT être focus d'emblée (PIN, MFA, saisie douchette) :
 * poser `data-allow-autofocus` sur le champ ou un ancêtre.
 *
 * Sur desktop/web rien ne change (le garde-fou n'est pas installé).
 */

/** Délai pendant lequel un focus est considéré comme voulu après un tap/touche. */
export const INTERACTION_WINDOW_MS = 600;

const NON_TEXT_INPUT_TYPES = new Set([
  'button',
  'checkbox',
  'color',
  'file',
  'hidden',
  'image',
  'radio',
  'range',
  'reset',
  'submit',
]);

/** Vrai si l'élément ouvre le clavier quand il prend le focus. */
export function isKeyboardEditable(el: Element | null): el is HTMLElement {
  if (!(el instanceof HTMLElement)) return false;
  if (el instanceof HTMLInputElement) return !NON_TEXT_INPUT_TYPES.has(el.type);
  if (el instanceof HTMLTextAreaElement) return true;
  return el.isContentEditable === true;
}

/** Champs qui affichent leur propre pavé (inputMode none / readOnly) : pas de clavier système. */
function opensSystemKeyboard(el: HTMLElement): boolean {
  if (el instanceof HTMLInputElement || el instanceof HTMLTextAreaElement) {
    if (el.readOnly) return false;
  }
  return el.inputMode !== 'none';
}

export interface KeyboardGuardOptions {
  /** Document cible (injectable pour les tests). */
  doc?: Document;
  /** Horloge (injectable pour les tests). */
  now?: () => number;
}

export interface KeyboardGuard {
  install: () => void;
  uninstall: () => void;
  /** Décision pure, exposée pour les tests. */
  shouldBlur: (el: Element | null) => boolean;
}

export function createKeyboardGuard(options: KeyboardGuardOptions = {}): KeyboardGuard {
  const doc = options.doc ?? document;
  const now = options.now ?? Date.now;
  let lastInteractionAt = Number.NEGATIVE_INFINITY;

  const markInteraction = (): void => {
    lastInteractionAt = now();
  };

  const shouldBlur = (el: Element | null): boolean => {
    if (!isKeyboardEditable(el)) return false;
    if (el.closest('[data-allow-autofocus]')) return false;
    if (!opensSystemKeyboard(el)) return false;
    return now() - lastInteractionAt > INTERACTION_WINDOW_MS;
  };

  const onFocusIn = (e: FocusEvent): void => {
    const target = e.target;
    if (target instanceof Element && shouldBlur(target)) {
      (target as HTMLElement).blur();
    }
  };

  const INTERACTION_EVENTS = ['pointerdown', 'touchstart', 'mousedown', 'keydown'] as const;

  return {
    install: () => {
      for (const ev of INTERACTION_EVENTS) doc.addEventListener(ev, markInteraction, true);
      doc.addEventListener('focusin', onFocusIn, true);
    },
    uninstall: () => {
      for (const ev of INTERACTION_EVENTS) doc.removeEventListener(ev, markInteraction, true);
      doc.removeEventListener('focusin', onFocusIn, true);
    },
    shouldBlur,
  };
}

/**
 * Installe le garde-fou une seule fois, uniquement en APK natif.
 * Retourne la fonction de désinstallation (no-op sur web).
 */
export function installNativeKeyboardGuard(): () => void {
  if (!isNativeApp()) return () => {};
  const guard = createKeyboardGuard();
  guard.install();
  return guard.uninstall;
}

/**
 * `autoFocus` de confort (desktop only). Sur device, le focus initial ne sert
 * qu'à faire surgir le clavier : on le désactive.
 */
export function desktopAutoFocus(): boolean {
  return !isNativeApp();
}

/**
 * Handler `onOpenAutoFocus` par défaut des overlays Radix (Dialog, Sheet,
 * AlertDialog, Popover, Drawer) : n'auto-focus jamais le premier champ.
 * Un appelant peut toujours passer son propre `onOpenAutoFocus`.
 */
export function preventOpenAutoFocus(event: Event): void {
  event.preventDefault();
}
